import { useState } from 'react'
import { SearchInput } from '@/components/ui/SearchInput'
import { Badge } from '@/components/ui/Badge'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Archive,
  ArchiveRestore,
  Building2,
  Check,
  ChevronDown,
  ChevronRight,
  MoreHorizontal,
  Pencil,
  Play,
  Plus,
  Settings,
  Timer,
} from 'lucide-react'
import { Container, ContainerContent, ContainerHeader, ContainerTitle } from '@/components/ui/Container'
import { Button } from '@/components/ui/Button'
import { Pagination } from '@/components/ui/Pagination'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import { toast } from '@/hooks/use-toast'
import { useOrganizationDetail } from '@/features/admin/organizationsQueries'
import { useUpdateWorkflow, useWorkflowList } from '@/features/admin/workflowsQueries'
import { WorkflowConnectionCards } from '@/features/admin/components/WorkflowConnectionCards'
import { ReadinessPill, READINESS_FILTERS } from '@/features/admin/components/ReadinessPill'
import { EditOrganizationDialog } from '@/features/admin/components/EditOrganizationDialog'
import { EditWorkflowDialog } from '@/features/admin/components/EditWorkflowDialog'
import { CreateWorkflowDialog } from '@/features/admin/components/CreateWorkflowDialog'
import { OrganizationCombobox } from '@/features/admin/components/OrganizationCombobox'
import { WORKFLOW_TYPES, type AdminWorkflow, type AdminWorkflowListItem } from '@/types/admin'
import { setActingAs } from '@/state/actingAs'
import { useIsRootOrganization } from '@/features/admin/useIsRootOrganization'
import type { ApiRequestError } from '@/services/api'

const ALL = 'all'

function capitalize(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1)
}

export function WorkflowListPage({ archived = false }: { archived?: boolean }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [expanded, setExpanded] = useState<string | null>(null)
  const [editWorkflow, setEditWorkflow] = useState<{ org: string; wf: AdminWorkflow } | null>(null)
  const [editOrgId, setEditOrgId] = useState<string | null>(null)
  const [createWorkflowOpen, setCreateWorkflowOpen] = useState(false)

  const page = Number(searchParams.get('page')) || 1
  const q = searchParams.get('q') ?? ''
  const organizationId = searchParams.get('organization') ?? ALL
  const type = searchParams.get('type') ?? ALL
  const audience = searchParams.get('audience') ?? ALL
  // The archive page is always exactly one status — its own filter would be
  // both redundant and a way to accidentally step out of it.
  const status = archived ? 'archived' : (searchParams.get('status') ?? ALL)
  const readiness = searchParams.get('readiness') ?? ALL

  const [searchInput, setSearchInput] = useState(q)

  const { data, isLoading } = useWorkflowList({
    page,
    q: q || undefined,
    organizationId: organizationId === ALL ? undefined : organizationId,
    type: type === ALL ? undefined : type,
    audience: audience === ALL ? undefined : audience,
    status: status === ALL ? undefined : status,
    readiness: readiness === ALL ? undefined : readiness,
  })

  // Only for the filter button's own label — the combobox searches
  // organizations itself, this just needs to show what's currently selected.
  const { data: selectedOrg } = useOrganizationDetail(organizationId === ALL ? undefined : organizationId)
  const { data: editingOrg } = useOrganizationDetail(editOrgId ?? undefined)

  function setParam(key: string, value: string) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (!value || value === ALL) next.delete(key)
        else next.set(key, value)
        next.delete('page')
        return next
      },
      { replace: true },
    )
  }

  const rows = data?.data ?? []

  return (
    <div className="container mx-auto py-4">
      <Container>
        <ContainerHeader className="flex flex-row items-center justify-between space-y-0">
          <ContainerTitle>{archived ? 'Archived workflows' : 'Workflows'}</ContainerTitle>
          <div className="flex items-center gap-2">
            {!archived && (
              <Button asChild variant="ghost" size="sm" className="text-gray-400 hover:text-gray-600">
                <Link to="/admin/workflows/archived">
                  <Archive className="h-3.5 w-3.5" />
                  Archived workflows
                </Link>
              </Button>
            )}
            {!archived && organizationId !== ALL && (
              <Button variant="cta" onClick={() => setCreateWorkflowOpen(true)}>
                <Plus className="h-4 w-4" />
                Create workflow
              </Button>
            )}
          </div>
        </ContainerHeader>

        <ContainerContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <form
              onSubmit={(e) => {
                e.preventDefault()
                setParam('q', searchInput)
              }}
            >
              <SearchInput
                className="w-[260px]"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search workflow or organization"
              />
            </form>

            <OrganizationCombobox
              className="h-[34px] w-[200px]"
              value={organizationId === ALL ? null : organizationId}
              selectedLabel={organizationId === ALL ? 'All organizations' : (selectedOrg?.name ?? '…')}
              onChange={(id) => setParam('organization', id ?? ALL)}
            />

            <Select value={type} onValueChange={(v) => setParam('type', v)}>
              <SelectTrigger className="h-[34px] w-[170px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All types</SelectItem>
                {WORKFLOW_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={audience} onValueChange={(v) => setParam('audience', v)}>
              <SelectTrigger className="h-[34px] w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All audiences</SelectItem>
                <SelectItem value="customer">Customer</SelectItem>
                <SelectItem value="test">Test</SelectItem>
              </SelectContent>
            </Select>

            {!archived && (
              <Select value={status} onValueChange={(v) => setParam('status', v)}>
                <SelectTrigger className="h-[34px] w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Any status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                </SelectContent>
              </Select>
            )}

            <Select value={readiness} onValueChange={(v) => setParam('readiness', v)}>
              <SelectTrigger className="h-[34px] w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Any readiness</SelectItem>
                {READINESS_FILTERS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-16 text-sm text-gray-400">
              Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-sm text-gray-400">
              {archived ? 'No archived workflows.' : 'No workflows match these filters.'}
            </div>
          ) : (
            <div className="data-sheet">
            <table className="w-full text-left">
              <thead>
                <tr className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <th className="w-8 px-2 pb-2 pt-3" />
                  <th className="px-4 pb-2 pt-3">Name</th>
                  <th className="px-4 pb-2 pt-3">Organization</th>
                  <th className="px-4 pb-2 pt-3">Type</th>
                  <th className="px-4 pb-2 pt-3">Audience</th>
                  <th className="px-4 pb-2 pt-3">Status</th>
                  <th className="px-4 pb-2 pt-3 text-center">
                    <span title="Scheduled workflow">
                      <Timer className="mx-auto h-4 w-4" />
                    </span>
                  </th>
                  <th className="px-4 pb-2 pt-3">Readiness</th>
                  <th className="w-10 px-2 pb-2 pt-3" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <Row
                    key={row.id}
                    row={row}
                    archived={archived}
                    expanded={expanded === row.id}
                    onToggle={() => setExpanded(expanded === row.id ? null : row.id)}
                    onEditOrg={() => setEditOrgId(row.organization_id)}
                    onEditWorkflow={() =>
                      setEditWorkflow({
                        org: row.organization_id,
                        wf: {
                          id: row.id,
                          name: row.name,
                          type: row.type,
                          audience: row.audience,
                          status: row.status,
                          start_mode: row.start_mode,
                        },
                      })
                    }
                  />
                ))}
              </tbody>
            </table>
            </div>
          )}

          {data && data.total_pages > 1 && (
            <Pagination
              page={data.page}
              totalCount={data.total}
              pageSize={data.page_size}
              onPage={(next) => setParam('page', String(next))}
            />
          )}
        </ContainerContent>
      </Container>

      <EditOrganizationDialog
        organization={editingOrg}
        open={!!editOrgId}
        onOpenChange={(open) => !open && setEditOrgId(null)}
      />
      {editWorkflow && (
        <EditWorkflowDialog
          organizationId={editWorkflow.org}
          workflow={editWorkflow.wf}
          onOpenChange={() => setEditWorkflow(null)}
        />
      )}
      {organizationId !== ALL && (
        <CreateWorkflowDialog
          organizationId={organizationId}
          open={createWorkflowOpen}
          onOpenChange={setCreateWorkflowOpen}
        />
      )}
    </div>
  )
}

function Row({
  row,
  archived,
  expanded,
  onToggle,
  onEditOrg,
  onEditWorkflow,
}: {
  row: AdminWorkflowListItem
  archived: boolean
  expanded: boolean
  onToggle: () => void
  onEditOrg: () => void
  onEditWorkflow: () => void
}) {
  const navigate = useNavigate()
  const updateWorkflow = useUpdateWorkflow(row.organization_id)
  const typeLabel = WORKFLOW_TYPES.find((t) => t.value === row.type)?.label ?? row.type

  // Acting as the organization, then straight into the workflow as they see it.
  // The banner is how you get back out.
  const { isRoot } = useIsRootOrganization()
  const setupPath = `/admin/organizations/${row.organization_id}/workflows/${row.id}/setup`

  function accessWorkflow() {
    setActingAs(row.organization_id, row.organization_name)
    navigate(`/workflow/${row.id}`)
  }

  async function archiveWorkflow() {
    if (!window.confirm('Really archive workflow? This will inactivate it.')) return
    try {
      await updateWorkflow.mutateAsync({ workflowId: row.id, name: row.name, status: 'archived' })
      toast({ title: 'Workflow archived' })
    } catch (error) {
      toast({
        variant: 'destructive',
        description: (error as ApiRequestError).message ?? 'Could not archive workflow',
      })
    }
  }

  async function restoreWorkflow() {
    try {
      await updateWorkflow.mutateAsync({ workflowId: row.id, name: row.name, status: 'draft' })
      toast({ title: 'Workflow restored as draft' })
    } catch (error) {
      toast({
        variant: 'destructive',
        description: (error as ApiRequestError).message ?? 'Could not restore workflow',
      })
    }
  }

  return (
    <>
      <tr
        className="cursor-pointer border-t border-gray-100 transition-colors hover:bg-gray-50"
        onClick={onToggle}
      >
        <td className="px-2 py-2.5 text-gray-400">
          {expanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </td>
        <td className="px-4 py-2.5 text-sm font-medium text-gray-900">{row.name}</td>
        <td className="px-4 py-2.5 text-sm text-gray-600">{row.organization_name}</td>
        <td className="px-4 py-2.5 text-sm text-gray-600">{typeLabel}</td>
        <td className="px-4 py-2.5 text-sm">
          <Badge tone={row.audience === 'customer' ? 'success' : 'warning'}>
            {row.audience === 'customer' ? 'Customer' : 'Test'}
          </Badge>
        </td>
        <td className="px-4 py-2.5 text-sm text-gray-600">{capitalize(row.status)}</td>
        <td className="px-4 py-2.5 text-center">
          {row.start_mode === 'scheduled' && (
            <Check className="mx-auto h-4 w-4 text-gray-600" aria-label="Scheduled" />
          )}
        </td>
        <td className="px-4 py-2.5">
          <ReadinessPill state={row.readiness} />
        </td>
        <td className="px-2 py-2.5" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-end gap-0.5">
            <button
              type="button"
              onClick={accessWorkflow}
              title="Access workflow"
              aria-label="Access workflow"
              className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-primary"
            >
              <Play className="h-4 w-4" />
            </button>
            <Link
              to={setupPath}
              title="Configuration"
              aria-label="Configuration"
              className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-primary"
            >
              <Settings className="h-4 w-4" />
            </Link>
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to={setupPath} className="flex items-center gap-2">
                  <Settings className="h-3.5 w-3.5" />
                  Configuration
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={accessWorkflow} className="flex items-center gap-2">
                <Play className="h-3.5 w-3.5" />
                Access workflow
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={onEditWorkflow} className="flex items-center gap-2">
                <Pencil className="h-3.5 w-3.5" />
                Rename workflow
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={onEditOrg} className="flex items-center gap-2">
                <Building2 className="h-3.5 w-3.5" />
                {isRoot ? 'Edit organization' : 'Rename organization'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {archived ? (
                <DropdownMenuItem onSelect={restoreWorkflow} className="flex items-center gap-2">
                  <ArchiveRestore className="h-3.5 w-3.5" />
                  Restore
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onSelect={archiveWorkflow}
                  className="flex items-center gap-2 text-red-600 focus:text-red-600"
                >
                  <Archive className="h-3.5 w-3.5" />
                  Archive
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </td>
      </tr>

      {/* Mounted only when open, so the three connections are fetched on expand. */}
      {expanded && (
        <tr className="border-t border-gray-100 bg-gray-50/50">
          <td colSpan={9} className="px-4 py-4">
            <WorkflowConnectionCards
              organizationId={row.organization_id}
              workflowId={row.id}
              workflowType={row.type}
              mode="summary"
            />
          </td>
        </tr>
      )}
    </>
  )
}
